import React from "react";
import { View, Text, Image, Pressable, StyleSheet, Modal } from "react-native";
import { textStyle, useScale } from "../styles";
import { trackButton, Button, View as MetricView } from "../services/metrics";

const icHandStop = require("../assets/icons/ic-hand-stop.png");
const icClose = require("../assets/icons/ic-close-outline.png");
const qrImage = require("../assets/icons/qr-parent-dashboard.png");

const MODAL_BG = "#F4F0E4";
const MODAL_ICON_BG = "#F1C033";
const TEXT_MODAL = "#242B46";
const BUTTON_BG = "#130A45";
const OVERLAY_BG = "rgba(0, 0, 0, 0.5)";

interface GrownUpsModalProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Parental-consent QR modal. Shared by the GrownUpsBanner "Scan Code" button and
 * the FTUE ready screen's "Create a new story" button (when access is blocked),
 * so both surface the identical window.
 */
export default function GrownUpsModal({ visible, onClose }: GrownUpsModalProps) {
  const { s } = useScale();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <View style={[styles.card, { borderRadius: s(48), paddingHorizontal: s(32), paddingTop: s(64), paddingBottom: s(32) }]}>
          <View style={[styles.cardIconOuter, { width: s(120), height: s(120), borderRadius: s(60), top: s(-60), borderWidth: s(8) }]}>
            <View style={[styles.cardIconInner, { width: s(104), height: s(104), borderRadius: s(52) }]}>
              <Image
                source={icHandStop}
                style={{ width: s(72), height: s(72) }}
                resizeMode="contain"
                tintColor="#FFFFFF"
              />
            </View>
          </View>

          <View style={[styles.cardBody, { gap: s(24) }]}>
            <View style={{ gap: s(8) }}>
              <Text style={styles.cardTitle}>You can't do this yet</Text>
              <Text style={styles.cardSubtitle}>
                Ask a grownup to turn this feature on for you from Parent Dashboard Settings.
              </Text>
            </View>

            <View style={[styles.qrSection, { gap: s(12) }]}>
              <Image
                source={qrImage}
                style={{ width: s(180), height: s(180), borderRadius: s(10) }}
                resizeMode="contain"
              />
              <Text style={styles.qrCaption} dataDetectorType="none">
                Scan the code to visit parents.amazon.com
              </Text>
            </View>

            <Pressable
              style={[styles.exitButton, { width: s(154), height: s(48), borderRadius: s(9999), paddingHorizontal: s(24) }]}
              onPress={() => { trackButton(MetricView.VPCEnablement, Button.VPCEnablement.Exit); onClose(); }}
              accessibilityRole="button"
              accessibilityLabel="Exit"
            >
              <Image
                source={icClose}
                style={{ width: s(24), height: s(24) }}
                resizeMode="contain"
                tintColor="#FFFFFF"
              />
              <Text style={styles.exitButtonText}>Exit</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: OVERLAY_BG,
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    backgroundColor: MODAL_BG,
    alignItems: "center",
    width: "42%",
  },
  cardIconOuter: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: MODAL_BG,
    borderColor: MODAL_BG,
  },
  cardIconInner: {
    backgroundColor: MODAL_ICON_BG,
    alignItems: "center",
    justifyContent: "center",
  },
  cardBody: {
    alignItems: "center",
    width: "100%",
  },
  cardTitle: {
    color: TEXT_MODAL,
    fontSize: 26,
    textAlign: "center",
    ...textStyle.bodyMedium,
  },
  cardSubtitle: {
    color: TEXT_MODAL,
    fontSize: 18,
    lineHeight: 22,
    textAlign: "center",
    ...textStyle.bodyMedium,
  },
  qrSection: {
    alignItems: "center",
  },
  qrCaption: {
    color: TEXT_MODAL,
    fontSize: 16,
    textAlign: "center",
    ...textStyle.heading,
  },
  exitButton: {
    backgroundColor: BUTTON_BG,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
  },
  exitButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    ...textStyle.heading,
  },
});
